import { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import type { ManualApprovalRecommendation, TradingMode } from "@shared/types";
import {
  getSupervisionQueue,
  approveManualRecommendation,
  rejectManualRecommendation,
  holdManualRecommendation,
  forceApproveManualRecommendation,
  bulkApproveManualRecommendations,
  bulkRejectManualRecommendations,
  getManualApprovalNotifications,
  setManualApprovalNotifications,
} from "@app/services/tradingSystemApi";
import { logger } from "@app/services/logger";

const HOLD_DURATIONS_MINUTES = [30, 60, 120] as const;
const QUEUE_REFRESH_MS = 15_000;

interface ManualApprovalPanelProps {
  currentMode: TradingMode;
  onModeChange: (mode: TradingMode) => void;
  modeChangePending?: boolean;
}

const MODE_OPTIONS: Array<{ value: TradingMode; label: string }> = [
  { value: "auto", label: "Auto" },
  { value: "manual", label: "Manual" },
  { value: "disabled", label: "Disabled" },
];

const MODE_STATUS_LABELS: Record<TradingMode, string> = {
  auto: "Auto (automated execution)",
  manual: "Manual (awaiting approval)",
  disabled: "Disabled",
};

function formatPrice(value: number | null | undefined): string {
  const n = Number(value);
  if (value == null || !Number.isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1) return n.toLocaleString(undefined, { maximumFractionDigits: 6 });
  return n.toLocaleString(undefined, { maximumFractionDigits: 8 });
}

function formatAge(generatedAt: string | null): string {
  if (!generatedAt) return "—";
  const ts = Date.parse(generatedAt);
  if (!Number.isFinite(ts)) return "—";
  const seconds = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

function toError(value: unknown): Error {
  return value instanceof Error ? value : new Error(String(value));
}

function toTradingViewSymbol(symbol: string): string {
  // Converts "ICNT/USDT" to "BYBIT:ICNTUSDT" (Bybit spot market on TradingView).
  const compact = symbol.replace("/", "");
  return `BYBIT:${compact}`;
}

function buildTradingViewChartUrl(symbol: string): string {
  // TradingView Advanced Chart widget embed (free). Keeps the toolbar visible so
  // the admin can switch timeframes, add indicators, and use basic drawing tools.
  const tvSymbol = encodeURIComponent(toTradingViewSymbol(symbol));
  const params = [
    `symbol=${tvSymbol}`,
    "interval=5",
    "hidesidetoolbar=0",
    "symboledit=1",
    "saveimage=1",
    "theme=dark",
    "style=1",
    "timezone=exchange",
    "withdateranges=1",
    "hide_side_toolbar=0",
    "allow_symbol_change=1",
    "details=1",
    "hotlist=0",
    "calendar=0",
    "show_popup_button=1",
    "popup_width=1000",
    "popup_height=650",
  ];
  return `https://s.tradingview.com/widgetembed/?${params.join("&")}`;
}

export function ManualApprovalPanel({
  currentMode,
  onModeChange,
  modeChangePending = false,
}: ManualApprovalPanelProps): JSX.Element {
  const [recommendations, setRecommendations] = useState<ManualApprovalRecommendation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  const [rejectTarget, setRejectTarget] = useState<ManualApprovalRecommendation | null>(null);
  const [holdTarget, setHoldTarget] = useState<ManualApprovalRecommendation | null>(null);
  const [forceApproveTarget, setForceApproveTarget] = useState<ManualApprovalRecommendation | null>(null);
  const [chartTarget, setChartTarget] = useState<ManualApprovalRecommendation | null>(null);

  const loadQueue = useCallback(async () => {
    try {
      const data = await getSupervisionQueue();
      setRecommendations(Array.isArray(data.recommendations) ? data.recommendations : []);
      setError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load supervision queue";
      logger.error("Manual approval queue load failed:", toError(err));
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadNotificationsState = useCallback(async () => {
    try {
      const data = await getManualApprovalNotifications();
      setNotificationsEnabled(Boolean(data.enabled));
    } catch (err) {
      logger.error("Failed to load manual approval notifications state:", toError(err));
    }
  }, []);

  useEffect(() => {
    void loadQueue();
    void loadNotificationsState();
    const intervalId = window.setInterval(() => {
      void loadQueue();
    }, QUEUE_REFRESH_MS);
    return () => window.clearInterval(intervalId);
  }, [loadQueue, loadNotificationsState]);

  const handleToggleNotifications = useCallback(async () => {
    setNotificationsLoading(true);
    try {
      const next = !notificationsEnabled;
      const data = await setManualApprovalNotifications(next);
      setNotificationsEnabled(Boolean(data.enabled));
    } catch (err) {
      logger.error("Failed to toggle manual approval notifications:", toError(err));
    } finally {
      setNotificationsLoading(false);
    }
  }, [notificationsEnabled]);

  const runAction = useCallback(
    async (id: string, action: () => Promise<unknown>) => {
      setProcessingIds((prev) => new Set(prev).add(id));
      try {
        await action();
      } catch (err) {
        logger.error(`Manual approval action failed for ${id}:`, toError(err));
        setError(err instanceof Error ? err.message : "Action failed");
      } finally {
        setProcessingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        await loadQueue();
      }
    },
    [loadQueue]
  );

  const handleApprove = useCallback(
    (id: string) => runAction(id, () => approveManualRecommendation(id)),
    [runAction]
  );

  const handleReject = useCallback(
    (id: string) => runAction(id, () => rejectManualRecommendation(id)),
    [runAction]
  );

  const handleHold = useCallback(
    (id: string, durationMinutes: number, reason: string) =>
      runAction(id, () => holdManualRecommendation(id, durationMinutes, reason)),
    [runAction]
  );

  const handleForceApprove = useCallback(
    (id: string, reason: string) =>
      runAction(id, () => forceApproveManualRecommendation(id, reason)),
    [runAction]
  );

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const handleBulkApprove = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      await bulkApproveManualRecommendations(ids);
    } catch (err) {
      logger.error("Bulk approve failed:", toError(err));
      setError(err instanceof Error ? err.message : "Bulk approve failed");
    } finally {
      clearSelection();
      await loadQueue();
    }
  }, [selectedIds, clearSelection, loadQueue]);

  const handleBulkReject = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      await bulkRejectManualRecommendations(ids, "bulk_reject");
    } catch (err) {
      logger.error("Bulk reject failed:", toError(err));
      setError(err instanceof Error ? err.message : "Bulk reject failed");
    } finally {
      clearSelection();
      await loadQueue();
    }
  }, [selectedIds, clearSelection, loadQueue]);

  const allSelected = useMemo(
    () => recommendations.length > 0 && recommendations.every((r) => selectedIds.has(r.id)),
    [recommendations, selectedIds]
  );

  const toggleSelectAll = useCallback(() => {
    setSelectedIds(allSelected ? new Set() : new Set(recommendations.map((r) => r.id)));
  }, [allSelected, recommendations]);

  return (
    <Card
      className="overview-card trading-system-card"
      title="Trades under supervision"
      subtitle="Recommendations that passed the Live Entry Gate and are waiting for your decision."
      actions={
        <div className="manual-approval-toolbar">
          <span
            className={`manual-approval-status manual-approval-status--${currentMode}`}
            role="status"
            aria-live="polite"
          >
            {MODE_STATUS_LABELS[currentMode] ?? MODE_STATUS_LABELS.auto}
          </span>
          <div className="manual-approval-mode" role="group" aria-label="Trading mode">
            {MODE_OPTIONS.map((option) => (
              <Button
                key={option.value}
                type="button"
                variant={currentMode === option.value ? "primary" : "ghost"}
                size="small"
                disabled={modeChangePending}
                onClick={() => onModeChange(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>
          <Button
            type="button"
            variant={notificationsEnabled ? "secondary" : "ghost"}
            size="small"
            disabled={notificationsLoading}
            onClick={() => void handleToggleNotifications()}
            title="Toggle Telegram alerts when a trade is held for manual approval"
          >
            {notificationsEnabled ? "Alerts: On" : "Alerts: Off"}
          </Button>
        </div>
      }
    >
      {error && (
        <div className="manual-approval-error">
          {error}
        </div>
      )}

      {selectedIds.size > 0 && (
        <div className="manual-approval-bulk-bar">
          <span>{selectedIds.size} selected</span>
          <Button type="button" variant="primary" size="small" onClick={() => void handleBulkApprove()}>
            Approve selected
          </Button>
          <Button type="button" variant="danger" size="small" onClick={() => void handleBulkReject()}>
            Reject selected
          </Button>
        </div>
      )}

      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  aria-label="Select all"
                />
              </th>
              <th>Symbol</th>
              <th>Entry</th>
              <th>Stop Loss</th>
              <th>Take Profit</th>
              <th>Confidence</th>
              <th>Age</th>
              <th>Source</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={10}>
                  <div className="empty-state">Loading supervision queue...</div>
                </td>
              </tr>
            ) : recommendations.length === 0 ? (
              <tr>
                <td colSpan={10}>
                  <div className="empty-state">
                    No trades waiting for manual approval.
                  </div>
                </td>
              </tr>
            ) : (
              recommendations.map((rec) => {
                const isProcessing = processingIds.has(rec.id);
                const isSelected = selectedIds.has(rec.id);
                return (
                  <tr key={rec.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelection(rec.id)}
                        aria-label={`Select ${rec.symbol}`}
                      />
                    </td>
                    <td>{rec.symbol}</td>
                    <td>{formatPrice(rec.entry)}</td>
                    <td>{formatPrice(rec.stop_loss)}</td>
                    <td title={(rec.take_profit ?? []).map(formatPrice).join(", ")}>
                      {(rec.take_profit ?? []).map(formatPrice).join(", ")}
                    </td>
                    <td>{rec.confidence != null ? `${(Number(rec.confidence) * 100).toFixed(0)}%` : "—"}</td>
                    <td>{formatAge(rec.generated_at)}</td>
                    <td>{rec.source || "—"}</td>
                    <td>
                      {rec.hold_active ? (
                        <span className="manual-approval-held">Held</span>
                      ) : (
                        <span className="manual-approval-pending">Pending</span>
                      )}
                    </td>
                    <td>
                      <div className="manual-approval-actions">
                        <Button
                          type="button"
                          variant="primary"
                          size="small"
                          disabled={isProcessing}
                          onClick={() => void handleApprove(rec.id)}
                        >
                          Approve
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          size="small"
                          disabled={isProcessing}
                          onClick={() => setChartTarget(rec)}
                        >
                          Chart
                        </Button>
                        <Button
                          type="button"
                          variant="danger"
                          size="small"
                          disabled={isProcessing}
                          onClick={() => setRejectTarget(rec)}
                        >
                          Reject
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="small"
                          disabled={isProcessing}
                          onClick={() => setHoldTarget(rec)}
                        >
                          Hold
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="small"
                          disabled={isProcessing}
                          onClick={() => setForceApproveTarget(rec)}
                        >
                          Force Approve
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <RejectModal
        recommendation={rejectTarget}
        onClose={() => setRejectTarget(null)}
        onConfirm={() => {
          const target = rejectTarget;
          setRejectTarget(null);
          if (target) void handleReject(target.id);
        }}
      />

      <HoldModal
        recommendation={holdTarget}
        onClose={() => setHoldTarget(null)}
        onConfirm={(duration, reason) => {
          const target = holdTarget;
          setHoldTarget(null);
          if (target) void handleHold(target.id, duration, reason);
        }}
      />

      <ForceApproveModal
        recommendation={forceApproveTarget}
        onClose={() => setForceApproveTarget(null)}
        onConfirm={(reason) => {
          const target = forceApproveTarget;
          setForceApproveTarget(null);
          if (target) void handleForceApprove(target.id, reason);
        }}
      />

      <ChartModal
        recommendation={chartTarget}
        onClose={() => setChartTarget(null)}
      />
    </Card>
  );
}

interface ModalProps {
  recommendation: ManualApprovalRecommendation | null;
  onClose: () => void;
}

function ModalOverlay({
  title,
  subtitle,
  children,
  onClose,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  onClose: () => void;
}): JSX.Element | null {
  return (
    <div
      className="manual-approval-modal-overlay"
      onClick={onClose}
    >
      <div
        className="manual-approval-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <h3>{title}</h3>
        <p className="manual-approval-modal-subtitle">{subtitle}</p>
        {children}
      </div>
    </div>
  );
}

function ReasonField({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}): JSX.Element {
  return (
    <textarea
      className="manual-approval-reason"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={3}
    />
  );
}

function ChartModal({ recommendation, onClose }: ModalProps): JSX.Element | null {
  const [expanded, setExpanded] = useState(false);
  const [minimized, setMinimized] = useState(false);

  useEffect(() => {
    if (recommendation) {
      setExpanded(false);
      setMinimized(false);
    }
  }, [recommendation]);

  if (!recommendation) return null;

  const chartUrl = buildTradingViewChartUrl(recommendation.symbol);

  const modalClass = [
    "manual-approval-chart-modal",
    expanded ? "manual-approval-chart-modal--expanded" : "",
    minimized ? "manual-approval-chart-modal--minimized" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="manual-approval-chart-overlay" onClick={onClose}>
      <div className={modalClass} onClick={(e) => e.stopPropagation()}>
        <div className="manual-approval-chart-header">
          <div className="manual-approval-chart-title">
            <h3>{recommendation.symbol} Chart</h3>
            {!minimized && (
              <p className="manual-approval-modal-subtitle">
                Powered by TradingView — switch timeframes and add indicators from the chart toolbar.
              </p>
            )}
          </div>
          <div className="manual-approval-chart-controls">
            <Button
              type="button"
              variant="ghost"
              size="small"
              title={minimized ? "Restore" : "Minimize"}
              onClick={() => setMinimized((v) => !v)}
            >
              {minimized ? "Restore" : "Minimize"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="small"
              title={expanded ? "Restore size" : "Expand to full tab"}
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? "Restore" : "Expand"}
            </Button>
            <Button type="button" variant="ghost" size="small" title="Close" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
        {!minimized && (
          <div className="manual-approval-chart-frame">
            <iframe
              src={chartUrl}
              title={`${recommendation.symbol} chart`}
              className="manual-approval-chart-iframe"
              frameBorder="0"
              allowFullScreen
            />
          </div>
        )}
      </div>
    </div>
  );
}

function RejectModal({ recommendation, onClose, onConfirm }: ModalProps & { onConfirm: () => void }): JSX.Element | null {
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (recommendation) setConfirming(false);
  }, [recommendation]);

  if (!recommendation) return null;

  return (
    <ModalOverlay
      title={`Reject ${recommendation.symbol}`}
      subtitle="This trade will be removed from the supervision queue and will not be executed."
      onClose={onClose}
    >
      <p className="manual-approval-confirm-text">
        Are you sure you want to reject this trade? This action cannot be undone.
      </p>
      <div className="manual-approval-modal-actions">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="danger"
          disabled={confirming}
          onClick={() => {
            setConfirming(true);
            onConfirm();
          }}
        >
          {confirming ? "Rejecting..." : "Confirm Reject"}
        </Button>
      </div>
    </ModalOverlay>
  );
}

function HoldModal({
  recommendation,
  onClose,
  onConfirm,
}: ModalProps & { onConfirm: (duration: number, reason: string) => void }): JSX.Element | null {
  const [duration, setDuration] = useState<number>(60);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (recommendation) {
      setDuration(60);
      setReason("");
    }
  }, [recommendation]);

  if (!recommendation) return null;

  const valid = reason.trim().length >= 3;

  return (
    <ModalOverlay
      title={`Hold ${recommendation.symbol}`}
      subtitle="Pin this trade so it is not auto-expired during the hold window."
      onClose={onClose}
    >
      <div className="manual-approval-durations">
        {HOLD_DURATIONS_MINUTES.map((minutes) => (
          <Button
            key={minutes}
            type="button"
            variant={duration === minutes ? "primary" : "ghost"}
            size="small"
            onClick={() => setDuration(minutes)}
          >
            {minutes}m
          </Button>
        ))}
      </div>
      <ReasonField
        value={reason}
        onChange={setReason}
        placeholder="Hold reason (min 3 characters)"
      />
      <div className="manual-approval-modal-actions">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          disabled={!valid}
          onClick={() => onConfirm(duration, reason.trim())}
        >
          Hold
        </Button>
      </div>
    </ModalOverlay>
  );
}

function ForceApproveModal({
  recommendation,
  onClose,
  onConfirm,
}: ModalProps & { onConfirm: (reason: string) => void }): JSX.Element | null {
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (recommendation) {
      setReason("");
      setConfirmed(false);
    }
  }, [recommendation]);

  if (!recommendation) return null;

  const valid = confirmed && reason.trim().length >= 3;

  return (
    <ModalOverlay
      title={`Force approve ${recommendation.symbol}`}
      subtitle="Override freshness validation and force this trade into execution. This action is audited."
      onClose={onClose}
    >
      <label className="manual-approval-confirm">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
        />
        I understand this bypasses validation and is recorded in the audit log.
      </label>
      <ReasonField
        value={reason}
        onChange={setReason}
        placeholder="Override reason (required)"
      />
      <div className="manual-approval-modal-actions">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="danger"
          disabled={!valid}
          onClick={() => onConfirm(reason.trim())}
        >
          Force Approve
        </Button>
      </div>
    </ModalOverlay>
  );
}
